import { useEffect, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Scissors, Eye, Image, Wand2, Download, X } from "lucide-react";

const TABS = ["Patterns", "Fittings", "Visualizations"];

const PATTERN_STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  generated: "bg-blue-100 text-blue-800",
  approved: "bg-emerald-100 text-emerald-800",
};

const formatDate = (dt) => {
  if (!dt) return "-";
  try {
    return new Date(dt).toLocaleDateString();
  } catch {
    return dt;
  }
};

const FIT_STYLE = {
  good: "bg-emerald-100 text-emerald-800",
  fair: "bg-amber-100 text-amber-800",
  poor: "bg-red-100 text-red-700",
};

export default function PatternEngineering() {
  const [tab, setTab] = useState("Patterns");
  const [patterns, setPatterns] = useState([]);
  const [fittings, setFittings] = useState([]);
  const [visualizations, setVisualizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [techpacks, setTechpacks] = useState([]);
  const [selectedTechpack, setSelectedTechpack] = useState("");
  const [generating, setGenerating] = useState(false);
  const [openPattern, setOpenPattern] = useState(null);
  const [openFitting, setOpenFitting] = useState(null);
  const [openViz, setOpenViz] = useState(null);
  const [fittingForm, setFittingForm] = useState({ fabric_weight: "", stretch_percentage: "", drape_setting: "" });

  const loadPatterns = () => {
    api.get("/pattern-records")
      .then((r) => setPatterns(r.data))
      .catch(() => {});
  };

  const loadFittings = () => {
    api.get("/fitting-records")
      .then((r) => setFittings(r.data))
      .catch(() => {});
  };

  const loadVisualizations = () => {
    api.get("/visualization-records")
      .then((r) => setVisualizations(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/pattern-records").then((r) => setPatterns(r.data)).catch(() => {}),
      api.get("/fitting-records").then((r) => setFittings(r.data)).catch(() => {}),
      api.get("/visualization-records").then((r) => setVisualizations(r.data)).catch(() => {}),
      api.get("/techpacks").then((r) => setTechpacks(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const reload = () => { loadPatterns(); loadFittings(); loadVisualizations(); }; // eslint-disable-line no-unused-vars

  const generatePattern = async () => {
    if (!selectedTechpack) { toast.error("Select a techpack first"); return; }
    setGenerating(true);
    try {
      await api.post(`/techpacks/${selectedTechpack}/generate-pattern`);
      toast.success("Pattern generated");
      loadPatterns();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const runVirtualFitting = async () => {
    if (!selectedTechpack) { toast.error("Select a techpack first"); return; }
    setGenerating(true);
    try {
      await api.post(`/techpacks/${selectedTechpack}/virtual-fitting`, {
        techpack_id: selectedTechpack,
        fabric_weight: fittingForm.fabric_weight ? parseFloat(fittingForm.fabric_weight) : null,
        stretch_percentage: fittingForm.stretch_percentage ? parseFloat(fittingForm.stretch_percentage) : null,
        drape_setting: fittingForm.drape_setting,
      });
      toast.success("Virtual fitting complete");
      loadFittings();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const generateVisualization = async () => {
    if (!selectedTechpack) { toast.error("Select a techpack first"); return; }
    setGenerating(true);
    try {
      await api.post(`/techpacks/${selectedTechpack}/generate-visualization`);
      toast.success("Visualization generated");
      loadVisualizations();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };


  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Pattern Engineering & Virtual Fitting</h1>
          <p className="text-zinc-500 mt-2 text-sm">Pattern records, virtual fittings, and AI visualizations.</p>
        </div>
      </div>

      {/* Generation controls */}
      <div className="mt-6 border border-zinc-200 bg-white rounded-lg p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-zinc-500 mb-1 block">Techpack</label>
            <select value={selectedTechpack} onChange={(e) => setSelectedTechpack(e.target.value)}
              className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              <option value="">Select techpack...</option>
              {techpacks.map((t) => <option key={t.id} value={t.id}>{t.style_name || t.style_number || t.id.slice(0, 8)}</option>)}
            </select>
          </div>
          {tab === "Fittings" && (
            <>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Fabric weight (g)</label>
                <input type="number" value={fittingForm.fabric_weight} onChange={(e) => setFittingForm({ ...fittingForm, fabric_weight: e.target.value })}
                  placeholder="200" className="w-28 border border-zinc-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Stretch %</label>
                <input type="number" value={fittingForm.stretch_percentage} onChange={(e) => setFittingForm({ ...fittingForm, stretch_percentage: e.target.value })}
                  placeholder="5" className="w-20 border border-zinc-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Drape</label>
                <input value={fittingForm.drape_setting} onChange={(e) => setFittingForm({ ...fittingForm, drape_setting: e.target.value })}
                  placeholder="soft" className="w-24 border border-zinc-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </>
          )}
          <button
            onClick={tab === "Patterns" ? generatePattern : tab === "Fittings" ? runVirtualFitting : generateVisualization}
            disabled={!selectedTechpack || generating || (tab === "Fittings" && (!fittingForm.fabric_weight || !fittingForm.stretch_percentage || !fittingForm.drape_setting)) || (tab === "Visualizations" && selectedTechpack && (() => { const tp = techpacks.find(t => t.id === selectedTechpack); return !tp?.category || !tp?.bom?.length || !tp?.colorways?.length; })())}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
            <Wand2 size={14} /> {generating ? "Generating..." : tab === "Patterns" ? "Generate Pattern" : tab === "Fittings" ? "Run Virtual Fitting" : "Generate Visualization"}
          </button>
        </div>
        {tab === "Fittings" && selectedTechpack && (!fittingForm.fabric_weight || !fittingForm.stretch_percentage || !fittingForm.drape_setting) && (
          <p className="text-xs text-amber-600 mt-2">All fabric properties (weight, stretch %, drape) are required to run a virtual fitting.</p>
        )}
        {tab === "Visualizations" && selectedTechpack && (() => { const tp = techpacks.find(t => t.id === selectedTechpack); const missing = []; if (!tp?.category) missing.push("garment category"); if (!tp?.bom?.length) missing.push("BOM materials"); if (!tp?.colorways?.length) missing.push("colorways"); return missing.length > 0 ? <p className="text-xs text-amber-600 mt-2">Visualization requires: {missing.join(", ")}. Update the tech pack first.</p> : null; })()}
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 overflow-x-auto pb-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${tab === t ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            {t} ({t === "Patterns" ? patterns.length : t === "Fittings" ? fittings.length : visualizations.length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : (
        <>
          {/* Patterns Tab */}
          {tab === "Patterns" && (
            patterns.length === 0 ? (
              <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
                <Scissors size={26} className="mx-auto mb-3" />
                <p className="text-sm">No pattern records yet.</p>
              </div>
            ) : (
              <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  <span className="col-span-4">Techpack ID</span>
                  <span className="col-span-2">Version</span>
                  <span className="col-span-2">Status</span>
                  <span className="col-span-2">DXF Files</span>
                  <span className="col-span-2 text-right">Created</span>
                </div>
                {patterns.map((p) => (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenPattern(p)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenPattern(p); } }}
                    className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-inset"
                  >
                    <span className="col-span-4 text-sm font-medium truncate" title={p.techpack_id}>{p.techpack_id.slice(0, 8)}...</span>
                    <span className="col-span-2 text-sm text-zinc-600">v{p.version}</span>
                    <span className="col-span-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${PATTERN_STATUS_STYLE[p.status] || PATTERN_STATUS_STYLE.draft}`}>
                        {p.status || "draft"}
                      </span>
                    </span>
                    <span className="col-span-2 text-sm text-zinc-600">
                      {(p.dxf_files || []).length} DXF
                      {(p.dxf_files || []).length > 0 && (
                        <span className="ml-1 text-xs text-zinc-400">
                          ({(p.dxf_files || []).reduce((n, f) => n + (f.piece_count || 0), 0)} pieces)
                        </span>
                      )}
                    </span>
                    <span className="col-span-2 text-sm text-zinc-500 text-right">{formatDate(p.created_at)}</span>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Fittings Tab */}
          {tab === "Fittings" && (
            fittings.length === 0 ? (
              <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
                <Eye size={26} className="mx-auto mb-3" />
                <p className="text-sm">No fitting records yet.</p>
              </div>
            ) : (
              <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  <span className="col-span-3">Techpack ID</span>
                  <span className="col-span-2">Drape Setting</span>
                  <span className="col-span-2">Fabric Weight</span>
                  <span className="col-span-2">Overall Fit</span>
                  <span className="col-span-3 text-right">Created</span>
                </div>
                {fittings.map((f) => {
                  const overallFit = (f.fit_assessment || {}).overall_fit || "-";
                  return (
                    <div
                      key={f.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenFitting(f)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenFitting(f); } }}
                      className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-inset"
                    >
                      <span className="col-span-3 text-sm font-medium truncate" title={f.techpack_id}>{f.techpack_id.slice(0, 8)}...</span>
                      <span className="col-span-2 text-sm text-zinc-600">{f.drape_setting || "-"}</span>
                      <span className="col-span-2 text-sm text-zinc-600">{f.fabric_weight != null ? `${f.fabric_weight}g` : "-"}</span>
                      <span className="col-span-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${FIT_STYLE[overallFit] || "bg-zinc-100 text-zinc-700"}`}>
                          {overallFit}
                        </span>
                      </span>
                      <span className="col-span-3 text-sm text-zinc-500 text-right">{formatDate(f.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Visualizations Tab */}
          {tab === "Visualizations" && (
            visualizations.length === 0 ? (
              <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
                <Image size={26} className="mx-auto mb-3" />
                <p className="text-sm">No visualization records yet.</p>
              </div>
            ) : (
              <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  <span className="col-span-3">Techpack ID</span>
                  <span className="col-span-2">Version</span>
                  <span className="col-span-3">Renders</span>
                  <span className="col-span-2">Prompt</span>
                  <span className="col-span-2 text-right">Created</span>
                </div>
                {visualizations.map((v) => {
                  const renders = v.renders || [];
                  const firstRender = renders.length > 0 ? renders[0] : null;
                  const imageUrl = firstRender ? (firstRender.image_url || firstRender.url || "") : "";
                  const cameraAngles = renders.map((r) => r.view || r.angle || "").filter(Boolean).join(", ");
                  // SOW Module 15 — AI-generated imagery is labelled as such wherever it appears.
                  const aiCount = renders.filter((r) => r.ai_generated).length;
                  const failed = renders.filter((r) => r.error);
                  return (
                    <div
                      key={v.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenViz(v)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenViz(v); } }}
                      className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-inset"
                    >
                      <span className="col-span-3 text-sm font-medium truncate" title={v.techpack_id}>{v.techpack_id.slice(0, 8)}...</span>
                      <span className="col-span-2 text-sm text-zinc-600">v{v.version}</span>
                      <span className="col-span-3 flex items-center gap-2">
                        {imageUrl ? (
                          <img src={imageUrl} alt="render" className="w-8 h-8 rounded border border-zinc-200 object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded border border-zinc-200 bg-zinc-50 flex items-center justify-center">
                            <Image size={12} className="text-zinc-300" />
                          </div>
                        )}
                        <span className="text-xs text-zinc-500">{cameraAngles || `${renders.length} render(s)`}</span>
                        {aiCount > 0 && (
                          <span
                            title={`${aiCount} of ${renders.length} render(s) generated by AI`}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200"
                          >
                            AI generated
                          </span>
                        )}
                        {failed.length > 0 && (
                          <span
                            title={failed.map((r) => `${r.view}: ${r.error}`).join("\n")}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200"
                          >
                            {failed.length} failed
                          </span>
                        )}
                      </span>
                      <span className="col-span-2 text-xs text-zinc-500 truncate" title={v.prompt_used}>{v.prompt_used || "-"}</span>
                      <span className="col-span-2 text-sm text-zinc-500 text-right">{formatDate(v.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </>
      )}
      <PatternDetail pattern={openPattern} onClose={() => setOpenPattern(null)} />
      <FittingDetail fitting={openFitting} onClose={() => setOpenFitting(null)} />
      <VisualizationDetail
        viz={openViz}
        onClose={() => setOpenViz(null)}
        onRegenerate={async () => {
          try {
            await api.post(`/techpacks/${openViz.techpack_id}/generate-visualization`);
            toast.success("Visualization regenerated");
            setOpenViz(null);
            loadVisualizations();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
      />
    </div>
  );
}

function PatternDetail({ pattern, onClose }) {
  if (!pattern) return null;
  const files = pattern.dxf_files || [];
  const download = (f) => {
    const href = `${BACKEND_URL}${f.url || `/api/pattern-records/${pattern.id}/download/${f.filename}`}`;
    window.open(href, "_blank", "noopener");
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="w-full max-w-2xl h-full overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-zinc-200">
          <div>
            <h2 className="text-lg font-semibold">Pattern v{pattern.version}</h2>
            <p className="text-xs text-zinc-500 font-mono mt-1">Tech pack {String(pattern.techpack_id).slice(0, 8)}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1 text-zinc-400 hover:text-zinc-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        {files.length === 0 ? (
          <div className="px-6 py-10 text-sm text-zinc-500">
            This pattern record has no DXF files. Regenerate it to draft the pieces.
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {files.map((f) => (
              <div key={f.filename} className="border border-zinc-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                  <div>
                    <p className="text-sm font-medium">Size {f.size}</p>
                    <p className="text-xs text-zinc-500 font-mono">
                      {f.filename}
                      {f.bytes ? ` · ${(f.bytes / 1024).toFixed(1)} KB` : ""}
                      {f.seam_allowance_cm != null ? ` · ${f.seam_allowance_cm}cm seam allowance` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => download(f)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-900 text-white hover:bg-zinc-700"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                      <th className="text-left font-normal px-4 py-2">Piece</th>
                      <th className="text-left font-normal px-4 py-2">Cut</th>
                      <th className="text-right font-normal px-4 py-2">Width</th>
                      <th className="text-right font-normal px-4 py-2">Height</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(f.pieces || []).map((piece) => (
                      <tr key={piece.piece} className="border-t border-zinc-100">
                        <td className="px-4 py-2 font-medium">
                          {piece.piece}
                          {piece.on_fold && <span className="ml-2 text-[10px] text-zinc-400 uppercase">on fold</span>}
                        </td>
                        <td className="px-4 py-2 text-zinc-600">{piece.cut_count}x</td>
                        <td className="px-4 py-2 text-right tabular-nums text-zinc-600">{piece.width_cm} cm</td>
                        <td className="px-4 py-2 text-right tabular-nums text-zinc-600">{piece.height_cm} cm</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(f.defaulted_poms || []).length > 0 && (
                  <p className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-100">
                    Drafted with standard-block defaults for: {f.defaulted_poms.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Shared shell for the detail drawers — same right-side panel the pattern detail uses. */
function DetailDrawer({ title, subtitle, onClose, children, actions }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="w-full max-w-2xl h-full overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-zinc-200">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            {subtitle && <p className="text-xs text-zinc-500 font-mono mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <button onClick={onClose} aria-label="Close" className="p-1 text-zinc-400 hover:text-zinc-900">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function FittingDetail({ fitting, onClose }) {
  if (!fitting) return null;
  const assessment = fitting.fit_assessment || {};
  const overall = assessment.overall_fit || "-";
  const areas = assessment.areas || [];
  const recommendations = assessment.recommendations || [];
  const images = fitting.rendered_images || [];

  return (
    <DetailDrawer
      title={`Fitting v${fitting.version}`}
      subtitle={`Tech pack ${String(fitting.techpack_id).slice(0, 8)} · ${formatDate(fitting.created_at)}`}
      onClose={onClose}
    >
      <div className="px-6 py-5 space-y-6">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.12em] text-zinc-400">Overall fit</span>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${FIT_STYLE[overall] || "bg-zinc-100 text-zinc-700"}`}>
            {overall}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4 border border-zinc-200 rounded-lg px-4 py-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">Fabric weight</p>
            <p className="text-sm mt-0.5 tabular-nums">{fitting.fabric_weight != null ? `${fitting.fabric_weight} g/m²` : "—"}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">Stretch</p>
            <p className="text-sm mt-0.5 tabular-nums">{fitting.stretch_percentage != null ? `${fitting.stretch_percentage}%` : "—"}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">Drape</p>
            <p className="text-sm mt-0.5">{fitting.drape_setting || "—"}</p>
          </div>
        </div>

        <div>
          <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-400 mb-2">
            Measurement areas ({areas.length})
          </h3>
          {areas.length === 0 ? (
            <p className="text-sm text-zinc-500">No areas were assessed.</p>
          ) : (
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.12em] text-zinc-400 bg-zinc-50">
                    <th className="text-left font-normal px-4 py-2">Area</th>
                    <th className="text-left font-normal px-4 py-2">Status</th>
                    <th className="text-left font-normal px-4 py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map((a, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="px-4 py-2 font-medium">{a.name || <span className="text-zinc-400 italic">unnamed</span>}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          a.status === "pass" ? "bg-emerald-100 text-emerald-800"
                            : a.status === "fail" ? "bg-red-100 text-red-700"
                            : "bg-zinc-100 text-zinc-700"}`}>
                          {a.status || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-zinc-600">{a.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {recommendations.length > 0 && (
          <div>
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-400 mb-2">Recommendations</h3>
            <ul className="space-y-1">
              {recommendations.map((r, i) => (
                <li key={i} className="text-sm text-zinc-700 pl-3 border-l-2 border-amber-300">{r}</li>
              ))}
            </ul>
          </div>
        )}

        {images.length > 0 && (
          <div>
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-400 mb-2">Rendered views</h3>
            <div className="grid grid-cols-3 gap-3">
              {images.map((img, i) => (
                <figure key={i} className="border border-zinc-200 rounded-lg overflow-hidden">
                  {img.image_url ? (
                    <img src={img.image_url} alt={img.view || "view"} className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-zinc-50" />
                  )}
                  <figcaption className="px-2 py-1.5 text-[11px] text-zinc-500 capitalize border-t border-zinc-100">
                    {(img.view || "view").replace(/_/g, " ")}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}

function VisualizationDetail({ viz, onClose, onRegenerate }) {
  const [busy, setBusy] = useState(false);
  if (!viz) return null;
  const renders = viz.renders || [];
  const prompt = viz.prompt_used || (renders[0] || {}).prompt || "";

  const regenerate = async () => {
    setBusy(true);
    try { await onRegenerate(); } finally { setBusy(false); }
  };

  return (
    <DetailDrawer
      title={`Visualization v${viz.version}`}
      subtitle={`Tech pack ${String(viz.techpack_id).slice(0, 8)} · ${formatDate(viz.created_at)}`}
      onClose={onClose}
      actions={
        <button
          onClick={regenerate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-50"
        >
          <Wand2 className="w-3.5 h-3.5" /> {busy ? "Regenerating…" : "Regenerate"}
        </button>
      }
    >
      <div className="px-6 py-5 space-y-6">
        {renders.length === 0 ? (
          <p className="text-sm text-zinc-500">This record has no renders.</p>
        ) : (
          <div className="space-y-5">
            {renders.map((r, i) => (
              <figure key={i} className="border border-zinc-200 rounded-lg overflow-hidden">
                {r.image_url ? (
                  <img src={r.image_url} alt={r.view || "render"} className="w-full object-contain bg-zinc-50" />
                ) : (
                  <div className="w-full h-48 bg-zinc-50 flex items-center justify-center">
                    <Image size={20} className="text-zinc-300" />
                  </div>
                )}
                <figcaption className="flex items-center justify-between gap-2 px-4 py-2 border-t border-zinc-100">
                  <span className="text-sm font-medium capitalize">{(r.view || "view").replace(/_/g, " ")}</span>
                  <span className="flex items-center gap-1.5">
                    {r.ai_generated && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">
                        AI generated
                      </span>
                    )}
                    {r.source_image_used && (
                      <span className="text-[10px] text-zinc-400">from flat drawing</span>
                    )}
                    {r.error && (
                      <span title={r.error} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                        failed
                      </span>
                    )}
                  </span>
                </figcaption>
                {r.error && <p className="px-4 py-2 text-xs text-red-700 bg-red-50 border-t border-red-100">{r.error}</p>}
              </figure>
            ))}
          </div>
        )}

        {prompt && (
          <div>
            <h3 className="text-[11px] uppercase tracking-[0.12em] text-zinc-400 mb-2">Prompt used</h3>
            <p className="text-xs leading-relaxed text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 whitespace-pre-wrap">
              {prompt}
            </p>
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}
